import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { getPublicOrganization, getPublicLocations, getPublicCity, getPublicMenus, getPublicCategories, getPublicProducts } from '../../shared/api/public.js'
import { getCart, updateCartItem, removeCartItem, clearCart } from '../../shared/api/cart.js'
import type { CheckoutResponseData } from '../../shared/api/cart.js'
import type { PublicOrganization, PublicLocation, PublicMenu, PublicCategory, PublicProduct, PublicCity } from '../../shared/api/public-types.js'
import type { CartDisplay } from './components/CartDrawer.js'
import ProductDetail from './components/ProductDetail.js'
import CartIcon from './components/CartIcon.js'
import CartDrawer from './components/CartDrawer.js'
import CheckoutForm from './components/CheckoutForm.js'
import OrderSuccess from './components/OrderSuccess.js'
import StorefrontClosedNotice from './components/StorefrontClosedNotice.js'
import { useStorefrontSchedule } from './useStorefrontSchedule.js'
import { buildClosedNoticeMessage } from '../../shared/utils/schedule.js'
import { cartUI } from '../../shared/store/cart.store.js'
import Skeleton, { SkeletonCard } from '../../shared/ui/Skeleton.js'
import { showToast } from '../../shared/ui/Toast.js'
import { addCartItem } from '../../shared/api/cart.js'

type PageStatus = 'loading' | 'ready' | 'error' | 'notFound'
type CartLoadStatus = 'idle' | 'loading' | 'ready' | 'error'
type StorefrontPhase = 'catalog' | 'checkout' | 'success'

// Contact fields survive PRICE_CHANGED → back-to-cart → re-checkout
interface CheckoutDraft {
  name: string
  phone: string
  email: string
  comment: string
  type: string
}

function cartToDisplay(src: {
  id: string
  items: { id: string; name: string; variantName: string | null; imageUrl: string | null; quantity: number; unitPrice: number; modifiers: { id: string; name: string; price: number; quantity: number }[]; totalPrice: number }[]
  total: number
}): CartDisplay {
  return {
    id: src.id,
    items: src.items.map(i => ({
      id: i.id,
      name: i.name,
      variantName: i.variantName,
      imageUrl: i.imageUrl,
      quantity: i.quantity,
      unitPrice: i.unitPrice,
      modifiers: i.modifiers.map(m => ({ id: m.id, name: m.name, price: m.price, quantity: m.quantity })),
      totalPrice: i.totalPrice,
    })),
    total: src.total,
  }
}

export default function StorefrontPage() {
  const { t, i18n } = useTranslation()
  const { citySlug, orgSlug, locationSlug } = useParams<{ citySlug: string; orgSlug: string; locationSlug: string }>()
  const resolvedSlug = orgSlug ?? citySlug ?? ''

  const [org, setOrg] = useState<PublicOrganization | null>(null)
  const [locations, setLocations] = useState<PublicLocation[]>([])
  const [selectedLoc, setSelectedLoc] = useState<PublicLocation | null>(null)
  const [menus, setMenus] = useState<PublicMenu[]>([])
  const [activeMenu, setActiveMenu] = useState<PublicMenu | null>(null)
  const [categories, setCategories] = useState<PublicCategory[]>([])
  const [activeCategory, setActiveCategory] = useState<string>('all')
  const [products, setProducts] = useState<PublicProduct[]>([])
  const [status, setStatus] = useState<PageStatus>('loading')
  const [notFoundReason, setNotFoundReason] = useState<'org' | 'city' | 'cityMismatch'>('org')
  const [detailProduct, setDetailProduct] = useState<PublicProduct | null>(null)
  const [menuLoading, setMenuLoading] = useState(false)
  const [catLoading, setCatLoading] = useState(false)
  const [prodLoading, setProdLoading] = useState(false)
  const [cartData, setCartData] = useState<CartDisplay | null>(null)
  const [cartStatus, setCartStatus] = useState<CartLoadStatus>('idle')
  const [cartError, setCartError] = useState<string | null>(null)
  const [phase, setPhase] = useState<StorefrontPhase>('catalog')
  const [lastOrder, setLastOrder] = useState<CheckoutResponseData | null>(null)
  const [checkoutDraft, setCheckoutDraft] = useState<CheckoutDraft>({ name: '', phone: '', email: '', comment: '', type: 'PICKUP' })
  const { schedule, noticePhase, orderingBlocked, acknowledgeNotice, refresh: refreshSchedule } = useStorefrontSchedule(
    resolvedSlug,
    selectedLoc?.slug ?? null,
  )
  const [noticeNow, setNoticeNow] = useState(() => new Date())

  useEffect(() => {
    if (!orderingBlocked) return
    setNoticeNow(new Date())
    const timer = window.setInterval(() => setNoticeNow(new Date()), 60_000)
    return () => window.clearInterval(timer)
  }, [orderingBlocked])

  const closedMessage = useMemo(() => {
    if (!orderingBlocked || !schedule || !org) return null
    return buildClosedNoticeMessage(t, org.name, schedule, noticeNow, i18n.language)
  }, [orderingBlocked, schedule, org, t, noticeNow, i18n.language])

  // Load organization. When the URL carries a city segment (/:citySlug/:orgSlug),
  // the city must exist, the organization must have locations in it, and any
  // explicit location slug must match that city. URLs without a city segment
  // (legacy deep links rendered directly) keep their previous behavior.
  useEffect(() => {
    if (!resolvedSlug) { setStatus('notFound'); return }
    let cancelled = false
    setStatus('loading')
    setNotFoundReason('org')
    ;(async () => {
      try {
        let city: PublicCity | null = null
        if (citySlug && orgSlug) {
          try {
            city = await getPublicCity(citySlug)
          } catch {
            if (cancelled) return
            setNotFoundReason('city')
            setStatus('notFound')
            return
          }
        }
        const [orgData, locs] = await Promise.all([
          getPublicOrganization(resolvedSlug),
          getPublicLocations(resolvedSlug),
        ])
        if (cancelled) return
        const activeCity = city
        const scopedLocs = activeCity ? locs.filter(l => l.city?.id === activeCity.id) : locs
        if (activeCity && scopedLocs.length === 0) {
          if (cancelled) return
          setNotFoundReason('cityMismatch')
          setStatus('notFound')
          return
        }
        setOrg(orgData)
        setLocations(scopedLocs)
        if (locationSlug) {
          const found = scopedLocs.find(l => l.slug === locationSlug) ?? null
          setSelectedLoc(found)
          if (!found) { setStatus('notFound'); return }
        } else if (scopedLocs.length === 1) {
          setSelectedLoc(scopedLocs[0]!)
        }
        setStatus('ready')
      } catch (err: unknown) {
        if (cancelled) return
        setStatus(err instanceof Error && err.message?.includes('404') ? 'notFound' : 'error')
      }
    })()
    return () => { cancelled = true }
  }, [resolvedSlug, locationSlug, citySlug, orgSlug])

  // Load menus when location selected
  useEffect(() => {
    if (!selectedLoc) return
    setMenuLoading(true)
    getPublicMenus(resolvedSlug, selectedLoc.slug!)
      .then(menus => {
        setMenus(menus)
        // Auto-select first menu if none selected
        if (menus.length > 0 && !activeMenu) {
          setActiveMenu(menus[0]!)
        }
      })
      .catch(() => setMenus([]))
      .finally(() => setMenuLoading(false))
  }, [selectedLoc])

  // Load categories when menu selected
  useEffect(() => {
    if (!activeMenu) return
    setCatLoading(true)
    setActiveCategory('all')
    getPublicCategories(resolvedSlug, selectedLoc!.slug!, activeMenu.id)
      .then(setCategories)
      .catch(() => setCategories([]))
      .finally(() => setCatLoading(false))
  }, [activeMenu])

  // Load products when menu selected
  useEffect(() => {
    if (!activeMenu) return
    setProdLoading(true)
    getPublicProducts(resolvedSlug, selectedLoc!.slug!, activeMenu.id)
      .then(setProducts)
      .catch(() => setProducts([]))
      .finally(() => setProdLoading(false))
  }, [activeMenu])

  // Load cart when location is selected
  useEffect(() => {
    if (!selectedLoc) return
    setCartStatus('loading')
    setCartError(null)
    getCart(resolvedSlug, selectedLoc.slug!)
      .then(src => { setCartData(cartToDisplay(src)); setCartStatus('ready') })
      .catch(err => { setCartError(err.message); setCartStatus('error') })
  }, [selectedLoc])

  function handleSelectLocation(loc: PublicLocation) {
    setSelectedLoc(loc)
    setActiveMenu(null)
    setCategories([])
    setProducts([])
  }

  function handleSelectMenu(menu: PublicMenu) {
    setActiveMenu(menu)
  }

  function handleQuickAdd(p: PublicProduct): void {
    if (orderingBlocked) return
    // If product has required options (multiple variants or required modifiers), open modal
    const hasRequiredMods = p.modifierGroups.some(g => g.isRequired)
    if (p.variants.length > 1 || hasRequiredMods) {
      setDetailProduct(p)
      return
    }
    // Disable button: guard against double-add
    if (cartStatus === 'loading') return
    setCartStatus('loading')
    addCartItem(resolvedSlug, selectedLoc!.slug!, {
      productId: p.id,
      variantId: p.baseVariant?.id,
      quantity: 1,
    })
      .then(src => { setCartData(cartToDisplay(src)); setCartStatus('ready'); showToast(t('storefront.addedToCart', { name: p.name })) })
      .catch(err => { setCartError(err.message); setCartStatus('error') })
  }

  function handleCartUpdateQty(itemId: string, qty: number) {
    if (qty < 1) return
    const currentItem = cartData?.items.find(item => item.id === itemId)
    if (orderingBlocked && currentItem && qty > currentItem.quantity) return
    setCartStatus('loading')
    updateCartItem(resolvedSlug, selectedLoc!.slug!, itemId, { quantity: qty })
      .then(src => { setCartData(cartToDisplay(src)); setCartStatus('ready') })
      .catch(err => { setCartError(err.message); setCartStatus('error') })
  }

  function handleCartRemove(itemId: string) {
    setCartStatus('loading')
    removeCartItem(resolvedSlug, selectedLoc!.slug!, itemId)
      .then(src => { setCartData(cartToDisplay(src)); setCartStatus('ready') })
      .catch(err => { setCartError(err.message); setCartStatus('error') })
  }

  function handleCartClear() {
    setCartStatus('loading')
    clearCart(resolvedSlug, selectedLoc!.slug!)
      .then(src => { setCartData(cartToDisplay(src)); setCartStatus('ready') })
      .catch(err => { setCartError(err.message); setCartStatus('error') })
  }

  async function handleCartAdd() {
    const src = await getCart(resolvedSlug, selectedLoc!.slug!)
    setCartData(cartToDisplay(src))
    setCartStatus('ready')
  }

  async function handleOrdersNotAccepting(details?: Record<string, unknown>): Promise<string> {
    const errorNextOpenTime = typeof details?.nextOpenTime === 'string' ? details.nextOpenTime : null
    const errorTimezone = typeof details?.timezone === 'string'
      ? details.timezone
      : (selectedLoc?.timezone ?? org?.timezone ?? 'UTC')

    const refreshed = await refreshSchedule()
    const nextOpenTime = refreshed ? refreshed.nextOpenTime : errorNextOpenTime
    const timezone = refreshed?.timezone ?? errorTimezone

    return buildClosedNoticeMessage(
      t,
      org?.name ?? '',
      { nextOpenTime, timezone },
      new Date(),
      i18n.language,
    )
  }

  function handleCheckoutClick() {
    cartUI.close()
    setPhase('checkout')
  }

  function handleCheckoutBack() {
    cartUI.open()
    setPhase('catalog')
  }

  function handleCheckoutSuccess(result: CheckoutResponseData) {
    setLastOrder(result)
    setPhase('success')
  }

  function handlePriceChanged() {
    setPhase('catalog')
    cartUI.open()
    // Refresh cart so drawer reflects current (backend) prices
    if (selectedLoc) {
      getCart(resolvedSlug, selectedLoc.slug!)
        .then(src => { setCartData(cartToDisplay(src)); setCartStatus('ready') })
        .catch(err => { setCartError(err.message); setCartStatus('error') })
    }
  }

  function handleBackToMenu() {
    setPhase('catalog')
    setLastOrder(null)
    // Backend cleared the cart on submit — refresh
    if (selectedLoc) {
      getCart(resolvedSlug, selectedLoc.slug!)
        .then(src => { setCartData(cartToDisplay(src)); setCartStatus('ready') })
        .catch(() => { setCartData(null); setCartStatus('ready') })
    }
  }

  const filteredProducts = activeCategory === 'all'
    ? products
    : products.filter(p => p.categoryId === activeCategory)

  // F5: Compute category counts for the full catalog (not filtered by category)
  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {}
    for (const p of products) {
      counts[p.categoryId] = (counts[p.categoryId] ?? 0) + 1
    }
    return counts
  }, [products])

  const totalCount = useMemo(() => products.length, [products])

  // F3: When viewing all products, group by category
  const groupedProducts = useMemo(() => {
    if (activeCategory !== 'all') return null
    const groups: { categoryName: string; products: PublicProduct[] }[] = []
    // Group products by categoryId
    const grouped = new Map<string, PublicProduct[]>()
    for (const p of products) {
      const arr = grouped.get(p.categoryId) ?? []
      arr.push(p)
      grouped.set(p.categoryId, arr)
    }
    // Maintain category order
    for (const cat of categories) {
      const catProducts = grouped.get(cat.id)
      if (catProducts) {
        groups.push({ categoryName: cat.name, products: catProducts })
      }
    }
    // Uncategorised products (shouldn't happen, but handle gracefully)
    const uncategorised = grouped.get('') ?? []
    if (uncategorised.length > 0) {
      groups.push({ categoryName: t('storefront.uncategorised'), products: uncategorised })
    }
    return groups
  }, [products, categories, activeCategory, t])

  if (status === 'loading') {
    return (
      <div className="storefront-layout">
        <header className="storefront-header">
          <div className="storefront-header-brand">
            <Skeleton width="48px" height="48px" shape="circle" />
            <div>
              <Skeleton width="120px" height="1.2em" className="skeleton-text" />
            </div>
          </div>
        </header>
        <main className="storefront-main">
          <div className="storefront-product-grid">
            {Array.from({ length: 6 }, (_, i) => <SkeletonCard key={i} />)}
          </div>
        </main>
      </div>
    )
  }

  if (status === 'notFound' || !org) {
    return (
      <div className="storefront-layout">
        <div className="storefront-not-found">
          <h1>
            {notFoundReason === 'city'
              ? t('storefront.cityNotFound')
              : notFoundReason === 'cityMismatch'
                ? t('storefront.cityMismatch')
                : t('storefront.notFound')}
          </h1>
          <p>{t('storefront.notFoundHint')}</p>
        </div>
      </div>
    )
  }

  if (status === 'error') {
    return (
      <div className="storefront-layout">
        <div className="storefront-error">
          <h1>{t('storefront.error')}</h1>
          <p>{t('storefront.errorHint')}</p>
        </div>
      </div>
    )
  }

  if (!selectedLoc && locations.length === 0) {
    return (
      <div className="storefront-layout">
        <div className="storefront-not-found">
          <h1>{t('storefront.noLocations')}</h1>
        </div>
      </div>
    )
  }

  // Location selection screen for multi-location
  if (!selectedLoc && locations.length > 0) {
    return (
      <div className="storefront-layout">
        <header className="storefront-header">
          {org.logoUrl && <img src={org.logoUrl} alt={org.name} className="storefront-logo" />}
          <h1 className="storefront-org-name">{org.name}</h1>
        </header>
        {org.coverUrl && <img src={org.coverUrl} alt={org.name} className="storefront-cover" />}
        <main className="storefront-main">
          <h2>{t('storefront.chooseLocation')}</h2>
          <div className="storefront-location-list">
            {locations.map(loc => (
              <button key={loc.id} className="storefront-location-card" onClick={() => handleSelectLocation(loc)}>
                <h3>{loc.name}</h3>
                {loc.address && <p>{loc.address}</p>}
              </button>
            ))}
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className="storefront-layout">
      {orderingBlocked && closedMessage && noticePhase !== 'none' && (
        <StorefrontClosedNotice
          message={closedMessage}
          phase={noticePhase}
          onAcknowledge={acknowledgeNotice}
        />
      )}

      <header className="storefront-header">
        <div className="storefront-header-brand">
          {org.logoUrl && <img src={org.logoUrl} alt={org.name} className="storefront-logo" />}
          <div>
            <h1 className="storefront-org-name">{org.name}</h1>
            {selectedLoc && <span className="storefront-loc-name">{selectedLoc.name}</span>}
          </div>
        </div>
        <div className="storefront-header-right">
          {locations.length > 1 && selectedLoc && (
            <select
              className="storefront-loc-select"
              value={selectedLoc.id}
              onChange={e => {
                const next = locations.find(l => l.id === e.target.value)
                if (next) handleSelectLocation(next!)
              }}
            >
              {locations.map(l => (
                <option key={l.id} value={l.id}>{l.name}</option>
              ))}
            </select>
          )}
          <CartIcon itemCount={cartData?.items.length ?? 0} />
        </div>
      </header>

      {org.coverUrl && <img src={org.coverUrl} alt={org.name} className="storefront-cover" />}

      {selectedLoc && (selectedLoc.address || selectedLoc.phone || selectedLoc.email) && (
        <div className="storefront-location-info">
          {selectedLoc.address && <span>{selectedLoc.address}</span>}
          {selectedLoc.phone && <a href={`tel:${selectedLoc.phone.replace(/[^\d+]/g, '')}`}>{selectedLoc.phone}</a>}
          {selectedLoc.email && <a href={`mailto:${selectedLoc.email}`}>{selectedLoc.email}</a>}
        </div>
      )}

      <main className="storefront-main">
        {phase === 'checkout' && selectedLoc && (cartData?.items.length ?? 0) > 0 && (
          <CheckoutForm
            orgSlug={resolvedSlug}
            locSlug={selectedLoc.slug!}
            currency={org.currency}
            cartItemsCount={cartData?.items.length ?? 0}
            cartTotal={cartData?.total ?? 0}
            orderAcceptance={selectedLoc.orderAcceptance}
            initialValues={checkoutDraft}
            onDraftChange={draft => setCheckoutDraft(draft)}
            onSuccess={handleCheckoutSuccess}
            onBack={handleCheckoutBack}
            onPriceChanged={handlePriceChanged}
            onOrdersNotAccepting={handleOrdersNotAccepting}
          />
        )}

        {phase === 'checkout' && (!cartData || cartData.items.length === 0) && (
          <div className="storefront-checkout-empty">
            <p>{t('checkout.errors.cartEmpty')}</p>
            <button type="button" className="btn btn-primary" onClick={handleBackToMenu}>
              {t('orderSuccess.backToMenu')}
            </button>
          </div>
        )}

        {phase === 'success' && lastOrder && (
          <OrderSuccess
            order={lastOrder}
            currency={org.currency}
            orgSlug={resolvedSlug}
            onBackToMenu={handleBackToMenu}
          />
        )}

        {phase === 'catalog' && (
          <>
        {org.description && <p className="storefront-org-description">{org.description}</p>}

        {menuLoading && <div className="storefront-loading"><div className="spinner" /></div>}

        {!menuLoading && menus.length > 1 && (
          <div className="storefront-menu-nav">
            {menus.map(m => (
              <button
                key={m.id}
                className={`storefront-menu-btn ${activeMenu?.id === m.id ? 'active' : ''}`}
                onClick={() => handleSelectMenu(m)}
              >
                {m.name}
              </button>
            ))}
          </div>
        )}

        {!menuLoading && menus.length === 0 && selectedLoc && (
          <p className="storefront-empty">{t('storefront.noMenus')}</p>
        )}

        {activeMenu && (
          <>
            {activeMenu.description && <p className="storefront-menu-desc">{activeMenu.description}</p>}

            {catLoading && <div className="storefront-loading"><div className="spinner" /></div>}

            {!catLoading && categories.length > 0 && (
              <div className="storefront-category-nav">
                <button
                  className={`storefront-cat-btn ${activeCategory === 'all' ? 'active' : ''}`}
                  onClick={() => setActiveCategory('all')}
                >
                  {t('storefront.allCategories')} ({totalCount})
                </button>
                {categories.map(c => (
                  <button
                    key={c.id}
                    className={`storefront-cat-btn ${activeCategory === c.id ? 'active' : ''}`}
                    onClick={() => setActiveCategory(c.id)}
                  >
                    {c.name} ({categoryCounts[c.id] ?? 0})
                  </button>
                ))}
              </div>
            )}

            {prodLoading && (
              <div className="storefront-product-grid">
                {Array.from({ length: 6 }, (_, i) => <SkeletonCard key={i} />)}
              </div>
            )}

            {!prodLoading && activeCategory === 'all' && groupedProducts && groupedProducts.length > 0 && (
              <div className="storefront-all-products">
                {groupedProducts.map(group => (
                  <section key={group.categoryName} className="storefront-category-section">
                    <h3 className="storefront-category-header">{group.categoryName}</h3>
                    <div className="storefront-product-grid">
                      {group.products.map(p => (
                        <div
                          key={p.id}
                          className="storefront-product-card"
                          onClick={() => setDetailProduct(p)}
                        >
                          {(p.images?.[0] ?? p.imageUrl) && <img src={p.images?.[0] ?? p.imageUrl!} alt={p.name} className="storefront-product-img" loading="lazy" />}
                          <div className="storefront-product-info">
                            <h3 className="storefront-product-name">{p.name}</h3>
                            {p.description && <p className="storefront-product-desc">{p.description}</p>}
                            {p.weight != null && <span className="storefront-product-weight">{p.weight} г</span>}
                            {p.weight == null && p.volume != null && <span className="storefront-product-weight">{p.volume} мл</span>}
                            <span className="storefront-product-price">
                              {p.baseVariant ? `${p.baseVariant.price} ₽` : ''}
                            </span>
                            <span
                              className="storefront-quick-add-wrap"
                              title={orderingBlocked ? closedMessage ?? undefined : undefined}
                              onClick={e => { if (orderingBlocked) e.stopPropagation() }}
                            >
                              <button
                                type="button"
                                className="storefront-quick-add"
                                aria-label={t('storefront.quickAdd', { name: p.name })}
                                aria-disabled={orderingBlocked}
                                title={orderingBlocked ? closedMessage ?? undefined : undefined}
                                disabled={orderingBlocked}
                                onClick={e => { e.stopPropagation(); handleQuickAdd(p) }}
                              >
                                🛒
                              </button>
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>
                ))}
              </div>
            )}

            {!prodLoading && activeCategory === 'all' && totalCount === 0 && (
              <p className="storefront-empty">{t('storefront.noProducts')}</p>
            )}

            {!prodLoading && activeCategory !== 'all' && filteredProducts.length === 0 && (
              <p className="storefront-empty">{t('storefront.noProducts')}</p>
            )}

            {!prodLoading && activeCategory !== 'all' && filteredProducts.length > 0 && (
              <div className="storefront-product-grid">
                {filteredProducts.map(p => (
                  <div
                    key={p.id}
                    className="storefront-product-card"
                    onClick={() => setDetailProduct(p)}
                  >
                    {(p.images?.[0] ?? p.imageUrl) && <img src={p.images?.[0] ?? p.imageUrl!} alt={p.name} className="storefront-product-img" loading="lazy" />}
                    <div className="storefront-product-info">
                      <h3 className="storefront-product-name">{p.name}</h3>
                      {p.description && <p className="storefront-product-desc">{p.description}</p>}
                      {p.weight != null && <span className="storefront-product-weight">{p.weight} г</span>}
                      {p.weight == null && p.volume != null && <span className="storefront-product-weight">{p.volume} мл</span>}
                      <span className="storefront-product-price">
                        {p.baseVariant ? `${p.baseVariant.price} ₽` : ''}
                      </span>
                      <span
                        className="storefront-quick-add-wrap"
                        title={orderingBlocked ? closedMessage ?? undefined : undefined}
                        onClick={e => { if (orderingBlocked) e.stopPropagation() }}
                      >
                        <button
                          type="button"
                          className="storefront-quick-add"
                          aria-label={t('storefront.quickAdd', { name: p.name })}
                          aria-disabled={orderingBlocked}
                          title={orderingBlocked ? closedMessage ?? undefined : undefined}
                          disabled={orderingBlocked}
                          onClick={e => { e.stopPropagation(); handleQuickAdd(p) }}
                        >
                          🛒
                        </button>
                      </span>
                    </div>
                  </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </>
        )}
      </main>

      <footer className="storefront-footer">
        <p>{org.name} &copy; {new Date().getFullYear()}</p>
      </footer>

      {detailProduct && selectedLoc && (
        <ProductDetail
          product={detailProduct}
          currency={org.currency}
          onClose={() => setDetailProduct(null)}
          orgSlug={resolvedSlug}
          locSlug={selectedLoc.slug!}
          onAddedToCart={handleCartAdd}
          orderingBlocked={orderingBlocked}
          blockedMessage={closedMessage}
        />
      )}

      <CartDrawer
        cart={cartData}
        loading={cartStatus === 'loading'}
        error={cartError}
        onUpdateQty={handleCartUpdateQty}
        onRemove={handleCartRemove}
        onClear={handleCartClear}
        onCheckout={handleCheckoutClick}
        orderingBlocked={orderingBlocked}
        blockedMessage={closedMessage}
      />
    </div>
  )
}